import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { CopyCommand } from "./CopyCommand";

const GLYPHS = "+xX80@#%$S";
const ROWS = 5;
const COLS = 22;

const randomRow = () =>
    Array.from({ length: COLS }, () => GLYPHS[Math.floor(Math.random() * GLYPHS.length)]).join(" ");

const AsciiField = () => {
    const [rows, setRows] = useState(() => Array.from({ length: ROWS }, randomRow));

    useEffect(() => {
        const id = setInterval(() => {
            setRows(Array.from({ length: ROWS }, randomRow));
        }, 140);
        return () => clearInterval(id);
    }, []);

    return (
        <div
            aria-hidden="true"
            data-testid="hero-ascii-field"
            className="pointer-events-none select-none font-mono text-[10px] leading-5 tracking-[0.35em] text-neutral-700"
        >
            {rows.map((row, i) => (
                <div key={i} style={{ opacity: 0.25 + Math.abs(i - (ROWS - 1) / 2) * 0.18 }}>
                    {row}
                </div>
            ))}
        </div>
    );
};

const Hero = () => (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden">
        <div className="mx-auto flex min-h-[88vh] max-w-6xl flex-col items-center justify-center px-4 py-24 text-center sm:px-6">
            <AsciiField />
            <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mt-10 font-mono text-xs uppercase tracking-[0.4em] text-green-400"
                data-testid="hero-eyebrow"
            >
                Procedural memory for AI agents
            </motion.p>
            <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                data-testid="hero-headline"
                className="mt-6 max-w-3xl text-4xl font-semibold tracking-tight text-neutral-50 sm:text-5xl lg:text-6xl"
            >
                Agents that get better with every run.
            </motion.h1>
            <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                data-testid="hero-subtext"
                className="mt-6 max-w-2xl text-sm leading-relaxed text-neutral-400 sm:text-base"
            >
                remembered.sh records the tool calls an agent made to finish a task and gives that
                procedure back the next time a similar task arrives. Fewer turns, fewer tool calls,
                same or better pass rate — and the memory stays on your own machine.
            </motion.p>
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="mt-10 flex flex-col items-center gap-4 sm:flex-row"
            >
                <CopyCommand testid="hero-copy-install-button" />
                <a
                    href="#how-it-works"
                    data-testid="hero-how-it-works-link"
                    className="font-mono text-xs text-neutral-500 underline-offset-4 transition-colors hover:text-green-400 hover:underline"
                >
                    see how it works ↓
                </a>
            </motion.div>
        </div>
        <div className="pointer-events-none absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2 font-mono text-[10px] uppercase tracking-[0.4em] text-neutral-600">
            scroll to continue
            <ChevronDown className="h-3 w-3 animate-bounce" />
        </div>
    </section>
);

export default Hero;
